import React, { useContext, useMemo, useRef } from 'react';
import type { AlohaWebsiteHeaderSettings } from '@shared/types/WebsiteHeaderSettings.types';
import { AuxFrame, AuxFrameContext } from '../AuxFrame';
import { HeaderPhoto } from './components/HeaderPhoto/HeaderPhoto';
import AuxFrameTitle from '../AuxFrame/components/AuxFrameTitle';
import { Box } from '@withjoy/joykit';
import { InlineEditor } from '@apps/guest/shared/components/InlineEditor';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { AnimatedContainer } from './components/AnimatedContainer/AnimatedContainer';
import { CoverPhoto } from './components/CoverPhoto/CoverPhoto';
import { isInIframe } from '@shared/utils/isInIframe';
import { useWelcomePage } from '@shared/hooks/useWelcomePage';

const isPreviewing = isInIframe();

export const CustomizableHeader: React.FC<{
  eventHandle: string;
  websiteHeader: NonNullable<AlohaWebsiteHeaderSettings['websiteHeader']>;
  telemetryPhotoEditor: {
    onEditPhotoOpenedChange: (opened: boolean) => void;
  };
  fontColor?: string;
}> = ({ children, websiteHeader }) => {
  const { page, pages, pageSlug, photo, pageId } = useContext(AuxFrameContext);
  const { checkIsWelcomePage } = useWelcomePage();
  const { isWelcomePage, welcomePageId } = useMemo(() => {
    const welcomePage = pages.find(page => checkIsWelcomePage(page.pageSlug, page.type).isWelcomePage);
    const welcomePageId = welcomePage?.id || '';
    return {
      isWelcomePage: pageId === welcomePageId,
      welcomePageId,
    };
  }, [pages, pageId, checkIsWelcomePage]);

  const handleEditPhotoClick = () => {
    // send the message to the parent window to open the photo editor
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: { action: 'editHeaderPhoto' },
    });
  };
  const handleEditCoverPhotoClick = () => {
    // send the message to the parent window to open the photo editor
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: { action: 'editPhoto', inlineEditData: { photoPresentation: null, enablePhotoLayout: false, photo, pageId, page, pageSlug } },
    });
  };

  // When previewing, skip the initial animation on first render for welcome page
  // to avoid flicker effect when loading the page for the first time.
  // For other pages, we want to keep the animation for better UX.
  // When not previewing, always show the animation.
  const hasSkippedInitialAnimationRef = useRef(isPreviewing && isWelcomePage ? false : true);
  const shouldSkipInitialAnimation = !hasSkippedInitialAnimationRef.current;
  if (shouldSkipInitialAnimation) {
    hasSkippedInitialAnimationRef.current = true;
  }

  return (
    <AuxFrame>
      {websiteHeader && (
        <>
          <Box zIndex={100}>
            <AuxFrameTitle
              x={'left'}
              y={'bottom'}
              textLight={true}
              title={websiteHeader?.eventDisplayName}
              subtitle={websiteHeader?.greetingMessage}
              welcomePageId={welcomePageId}
              page={page}
              pageSlug={pageSlug || ''}
              zIndex={1}
            />
          </Box>
          <InlineEditor
            elementLabel="Photo"
            wrapperType="actionInside"
            actionData={{
              editPhoto: isWelcomePage ? handleEditPhotoClick : handleEditCoverPhotoClick,
            }}
            minHeight="100vh"
            componentName="headerPhoto"
            pageName={page}
            pageSlug={pageSlug || ''}
          >
            <AnimatedContainer
              skipAnimation={shouldSkipInitialAnimation}
              id={isWelcomePage ? 'header-photo' : photo?.id || ''}
            >
              {isWelcomePage ? (
                <HeaderPhoto headerPhoto={websiteHeader?.headerPhoto} />
              ) : photo ? (
                <CoverPhoto photo={photo} />
              ) : (
                <Box
                  width="100%"
                  height="100vh"
                />
              )}
            </AnimatedContainer>
          </InlineEditor>
        </>
      )}
      {children}
    </AuxFrame>
  );
};
